import { useCallback, useRef, useState } from "react";
import { W, H } from "./constants";

// Pan/zoom viewBox state + pointer handlers. Pointer capture is deferred until a real
// drag (>3px) so clicks on child pins/pulses still reach their handlers.
export function useMapView(svgRef) {
  const [view, setView] = useState({ x: 0, y: 0, w: W, h: H });
  const viewRef = useRef(view);
  viewRef.current = view;
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, vx: 0, vy: 0, moved: false });

  // With preserveAspectRatio="slice" on a portrait phone only a centred band of the viewBox is
  // visible, so clamp the *visible* region to the world instead of the raw viewBox.
  const clampView = useCallback((v) => {
    const rect = svgRef.current?.getBoundingClientRect();
    const boxAspect = rect && rect.height ? rect.width / rect.height : W / H;
    const visW = Math.min(v.w, v.h * boxAspect);
    const visH = Math.min(v.h, v.w / boxAspect);
    const padX = (v.w - visW) / 2, padY = (v.h - visH) / 2;
    return {
      ...v,
      x: Math.max(-padX, Math.min(W - v.w + padX, v.x)),
      y: Math.max(-padY, Math.min(H - v.h + padY, v.y)),
    };
  }, [svgRef]);

  const zoom = useCallback((factor, centerX, centerY) => {
    setView((v) => {
      const newW = Math.max(150, Math.min(W, v.w / factor));
      const newH = newW * (H / W);
      const rx = centerX / W, ry = centerY / H;
      return clampView({ x: v.x + (v.w - newW) * rx, y: v.y + (v.h - newH) * ry, w: newW, h: newH });
    });
  }, [clampView]);

  // Rendered scale (px per viewBox unit). With preserveAspectRatio="slice" the larger axis wins.
  const scaleOf = (rect) => Math.max(rect.width / view.w, rect.height / view.h);

  const toViewCoords = (e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const s = scaleOf(rect);
    return {
      x: view.x + (view.w - rect.width / s) / 2 + (e.clientX - rect.left) / s,
      y: view.y + (view.h - rect.height / s) / 2 + (e.clientY - rect.top) / s,
    };
  };

  const onWheel = (e) => {
    e.preventDefault();
    const p = toViewCoords(e);
    if (p) zoom(e.deltaY < 0 ? 1.2 : 1 / 1.2, p.x, p.y);
  };
  const onPointerDown = (e) => {
    dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, vx: view.x, vy: view.y, moved: false, pointerId: e.pointerId, capturingTarget: e.currentTarget };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.dragging) return;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const s = scaleOf(rect);
    const dx = (e.clientX - dragRef.current.startX) / s;
    const dy = (e.clientY - dragRef.current.startY) / s;
    if (Math.abs(dx) + Math.abs(dy) > 3 && !dragRef.current.moved) {
      dragRef.current.moved = true;
      dragRef.current.capturingTarget?.setPointerCapture?.(dragRef.current.pointerId);
    }
    if (!dragRef.current.moved) return;
    setView((v) => clampView({ ...v, x: dragRef.current.vx - dx, y: dragRef.current.vy - dy }));
  };
  const onPointerUp = () => {
    if (dragRef.current.moved) {
      dragRef.current.capturingTarget?.releasePointerCapture?.(dragRef.current.pointerId);
    }
    dragRef.current.dragging = false;
  };
  const flyRef = useRef(null);
  const resetView = () => { if (flyRef.current) cancelAnimationFrame(flyRef.current); setView({ x: 0, y: 0, w: W, h: H }); };
  const wasDragged = () => dragRef.current.moved;

  // Smoothly glide the viewport so (px, py) lands centered at `factor`× zoom.
  const flyTo = useCallback((px, py, factor = 3, duration = 1100) => {
    if (flyRef.current) cancelAnimationFrame(flyRef.current);
    const tw = Math.max(150, Math.min(W, W / factor));
    const th = tw * (H / W);
    const target = clampView({ x: px - tw / 2, y: py - th / 2, w: tw, h: th });
    const from = { ...viewRef.current };
    const t0 = performance.now();
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const tick = (now) => {
      const k = ease(Math.min(1, (now - t0) / duration));
      setView({
        x: from.x + (target.x - from.x) * k,
        y: from.y + (target.y - from.y) * k,
        w: from.w + (target.w - from.w) * k,
        h: from.h + (target.h - from.h) * k,
      });
      if (k < 1) flyRef.current = requestAnimationFrame(tick);
    };
    flyRef.current = requestAnimationFrame(tick);
  }, [clampView]);

  return {
    view, zoom, resetView, flyTo, toViewCoords, wasDragged,
    svgHandlers: { onWheel, onPointerDown, onPointerMove, onPointerUp, onPointerLeave: onPointerUp },
  };
}
